import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import { formatCurrency } from '@/libs/antd/formater';
import { useViewPurchaseOrderHistoryQuery } from '@/libs/fleet-api/services/print-disbursement';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Divider, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment } from 'react';

export type DisbursementPOHistoryProps = {
	id: number;
	onBack?: () => void;
};

const DisbursementPOHistory: FC<DisbursementPOHistoryProps> = ({
	id,
	onBack,
}) => {
	const { navigate } = useApp();
	const { openCustomerDetail, openCustomerGroupDetail } = useModals();

	const { data, isFetching } = useViewPurchaseOrderHistoryQuery(id);
	const {
		items = [],
		mainInformation,
		ntfTotal,
		ntfGoLive,
		ntfVerified,
		ntfInVerificationProcess,
		ntfNotYetRequstedTotal,
	} = data?.data ?? {};

	const {
		customerName,
		customerId,
		bmppGroupId,
		bmppGroupName,
		mppId,
		mppNo,
		mppDate,
	} = mainInformation ?? {};

	const dataSource = items.map((item, key) => ({ ...item, key }));

	return (
		<Fragment>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				{typeof onBack === 'function' && (
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => onBack?.()}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
				)}
				<div style={{ flex: 1 }}>
					<FleetTitle>View Historical Purchase Order</FleetTitle>
				</div>
			</div>

			<DataRow
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!customerId) return;
										openCustomerDetail(customerId);
									}}
								>
									{customerName}
								</a>
							) : (
								'-'
							),
						],
						[
							'Group Customer BMPP',
							bmppGroupId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!bmppGroupId) return;
										openCustomerGroupDetail(bmppGroupId);
									}}
								>
									{bmppGroupName}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'No. MPP',
							mppNo && mppId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('MPPPage', {
											mppId,
											viewOnly: true,
											onBack: () => {
												navigate('DisbursementPOHistory', {
													id,
													onBack,
												});
											},
										});
									}}
								>
									{mppNo}
								</a>
							) : (
								'-'
							),
						],
						[
							'Tanggal MPP',
							mppDate ? dayjs(mppDate).format('DD/MM/YYYY') : '-',
						],
					],
				]}
			/>

			<Divider />

			<Typography.Title level={3}>Rincian Purchase Order</Typography.Title>

			<Table
				bordered
				scroll={{ x: 'max-content' }}
				loading={isFetching}
				pagination={false}
				dataSource={dataSource}
				columns={[
					{
						title: 'No',
						align: 'center',
						width: 1,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'No. Simulasi',
						render: (_, record) =>
							record.simulationNo ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('SimulationPage', {
											simulationId: record.simulationId,
											showResult: true,
											viewOnly: true,
											onBack: () => {
												navigate('DisbursementPOHistory', {
													id,
													onBack,
												});
											},
										});
									}}
									style={{ cursor: 'pointer' }}
								>
									{record.simulationNo}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'No. Purchase Order',
						render: (_, record) => record.purchaseOrderNo ?? '-',
					},
					{
						title: 'Tanggal Pencetakan',
						render: (_, record) =>
							record.purchaseOrderDate
								? dayjs(record.purchaseOrderDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'Action',
						render: (_, record) => record.action ?? '-',
					},
					{
						title: 'Tanggal Action',
						render: (_, record) =>
							record.actionDate
								? dayjs(record.actionDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'Status',
						render: (_, record) => record.purchaseOrderStatus ?? '-',
					},
					{
						title: 'NTF',
						render: (_, record) =>
							record.ntf ? formatCurrency(record.ntf) : '-',
					},
				]}
			/>

			<Table
				style={{ marginTop: 40, borderTop: '5px solid #1557BC' }}
				bordered
				showHeader={false}
				dataSource={[
					{
						title: 'Total NTF',
						value: 'Rp ' + formatCurrency(ntfTotal),
					},
					{
						title: 'Total NTF dalam Proses Verifikasi ',
						value: 'Rp ' + formatCurrency(ntfInVerificationProcess),
					},
					{
						title: 'Total NTF yang Sudah di Verifikasi',
						value: 'Rp ' + formatCurrency(ntfVerified),
					},
					{
						title: 'Total NTF yang Sudah Golive',
						value: 'Rp ' + formatCurrency(ntfGoLive),
					},
					{
						title: 'Total NTF yang Belum Request',
						value: 'Rp ' + formatCurrency(ntfNotYetRequstedTotal),
					},
				]}
				columns={[
					{
						align: 'left',
						width: '50%',
						render: (_, record, index) =>
							index ? (
								record.title
							) : (
								<b style={{ color: '#1557BC' }}>{record.title}</b>
							),
					},
					{
						align: 'right',
						width: '50%',
						render: (_, record, index) => (
							<b style={{ color: index ? 'currentcolor' : '#1557BC' }}>
								{record.value}
							</b>
						),
					},
				]}
			/>
		</Fragment>
	);
};

export default DisbursementPOHistory;
